import React, { useCallback, useMemo } from 'react';

import _ from 'lodash';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { getAirlineForInitials } from 'utils/generic';

import { Checkbox, CheckboxLabel, CheckboxCount, AirIcon } from './styles';

interface Props {
  airlines: { text: string; count: number }[];
  values: string[];
  onChange: (values: string[]) => void;
  onOptionChange?: (value: string, isChecked: boolean) => void;
}

const Airlines = ({ airlines, values, onChange, onOptionChange }: Props) => {
  const { t } = useStoreTranslation();

  const isChecked = useCallback((checkValue: string) => Boolean(values.find((item) => item === checkValue)), [values]);

  const handleChange = (newValue: string) => {
    if (isChecked(newValue)) {
      onChange(values.filter((item) => item !== newValue));
    } else {
      onChange([...values, newValue]);
    }
    if (onOptionChange) onOptionChange(newValue, !isChecked(newValue));
  };

  const sortedAirlines = useMemo(
    () =>
      _.sortBy(
        _.orderBy(airlines, [(item) => item.count, (item) => item.text.toLowerCase()], ['desc', 'asc']),
        (item) => (item.text === 'MultipleCarriers' ? 0 : 1),
      ).filter((airline, index, self) => index === self.findIndex((t) => t.text === airline.text)),
    [airlines],
  );

  const airlineLabel = (text?: string) =>
    text ? (
      <>
        <AirIcon src={`https://images.rappi.com/web/travel/airlines/${text}.png`} /> {getAirlineForInitials(text)}
      </>
    ) : null;

  return (
    <section>
      {sortedAirlines.map((airline, index) => (
        <Checkbox key={index} checked={isChecked(airline.text)} onChange={() => handleChange(airline.text)}>
          <CheckboxLabel
            onClick={() => handleChange(airline.text)}
            content={
              airline.text === 'MultipleCarriers' ? t('flightsFilters.airlines.multiple') : airlineLabel(airline.text)
            }
          />
          <CheckboxCount>{airline.count}</CheckboxCount>
        </Checkbox>
      ))}
    </section>
  );
};

export default Airlines;
